import { useMemo, useState } from "react";
import { ChevronDown, Hash, Lock, MessageSquare, Search, User, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { ApiConversation, ApiUser } from "../lib/api";
import { Skeleton } from "./ui/Skeleton";
import { EmptyState } from "./ui/EmptyState";
import { buildHandleMap, formatGroupDmLabel } from "../lib/groupDmName";
import { gradientCSSFor } from "../lib/avatarGradient";
import type { HealthColor } from "../lib/workspaceHealth";

interface ConversationListProps {
  /** Workspace name for the header. */
  workspaceName: string;
  /** team_id displayed under the name and used for the
   *  workspace-info trigger's data-testid suffix. */
  workspaceTeamID: string;
  /** Conversations as returned by the service — empty array is
   *  valid (workspace ingested but no conversations have any
   *  messages yet). */
  conversations: ApiConversation[];
  activeConversationID: string | null;
  onSelect: (conversationID: string) => void;
  /** Optional info-panel toggle, kept from Pass 1.6. */
  onToggleInfo?: () => void;
  infoOpen?: boolean;
  /** Loading + error states surface from the parent's hook so
   *  the UI can show a skeleton or an inline error rather than
   *  an empty list when something's wrong. */
  loading?: boolean;
  error?: string | null;
  /** S3.2: workspace user cache (keyed by user_id) used to resolve
   *  group-DM (mpim) names from raw "mpdm-…" handles to display
   *  names. Optional — falls back to the raw label when absent. */
  users?: Record<string, ApiUser>;
  /** Pass 2.12: pixel-native width set by BrowseView from
   *  useLayoutState. Was previously the Tailwind w-72 (288px)
   *  fixed class; now driven by the resizable layout. */
  width: number;
  /** Aurora: workspace health from /system/status — renders the
   *  glow dot next to the workspace name (Figma). Optional so the
   *  component stays renderable without the status provider. */
  healthColor?: HealthColor | null;
}

type ConversationType = ApiConversation["type"];

interface SectionDef {
  type: ConversationType;
  label: string;
}

const SECTIONS: readonly SectionDef[] = [
  { type: "public_channel", label: "Channels" },
  { type: "private_channel", label: "Private channels" },
  { type: "mpim", label: "Group DMs" },
  { type: "im", label: "Direct messages" },
];

const TYPE_ICON: Record<ConversationType, LucideIcon> = {
  public_channel: Hash,
  private_channel: Lock,
  mpim: Users,
  im: User,
};

function sortConversations(items: ApiConversation[]): ApiConversation[] {
  return items.slice().sort((a, b) => {
    const am = (a.message_count || 0) > 0 ? 0 : 1;
    const bm = (b.message_count || 0) > 0 ? 0 : 1;
    if (am !== bm) return am - bm;
    return (a.label || "").localeCompare(b.label || "", undefined, {
      sensitivity: "base",
    });
  });
}

export function ConversationList({
  workspaceName,
  workspaceTeamID,
  conversations,
  activeConversationID,
  onSelect,
  onToggleInfo,
  infoOpen = false,
  loading = false,
  error = null,
  users = {},
  width,
  healthColor = null,
}: ConversationListProps) {
  // S3.2: handle → display-name map for resolving group-DM labels.
  const handleMap = useMemo(() => buildHandleMap(users), [users]);

  // Aurora: client-side conversation filter (the Figma's "Filter
  // conversations" affordance, functional).
  const [filter, setFilter] = useState("");

  const grouped = useMemo(() => {
    const out: Record<ConversationType, ApiConversation[]> = {
      public_channel: [],
      private_channel: [],
      mpim: [],
      im: [],
    };
    for (const c of conversations) {
      const bucket = out[c.type];
      if (bucket) bucket.push(c);
    }
    for (const t of Object.keys(out) as ConversationType[]) {
      out[t] = sortConversations(out[t]);
    }
    return out;
  }, [conversations]);

  const visibleGrouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return grouped;
    const out: Record<ConversationType, ApiConversation[]> = {
      public_channel: [],
      private_channel: [],
      mpim: [],
      im: [],
    };
    for (const t of Object.keys(grouped) as ConversationType[]) {
      out[t] = grouped[t].filter((c) => {
        const lbl =
          c.type === "mpim" ? formatGroupDmLabel(c.label, handleMap) : c.label;
        return (lbl || "").toLowerCase().includes(q);
      });
    }
    return out;
  }, [grouped, filter, handleMap]);
  const filterEmpty =
    filter.trim().length > 0 &&
    (Object.values(visibleGrouped) as ApiConversation[][]).every(
      (xs) => xs.length === 0,
    );

  return (
    <nav
      data-testid="conversation-list"
      aria-label={`${workspaceName} conversations`}
      // Pass 2.12: width is pixel-native via inline style.
      // CRITICAL: do NOT add a Tailwind w-* class here — it would
      // win against the inline style via specificity (this is
      // exactly the trap Pass 2.11 fell into).
      style={{ width }}
      className="bg-white/[0.02] border-r border-white/[0.06] flex flex-col flex-shrink-0 min-h-0"
    >
      <div className="px-4 py-4 border-b border-white/[0.06] flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          {onToggleInfo ? (
            <button
              type="button"
              onClick={onToggleInfo}
              data-testid={`workspace-info-trigger-${workspaceTeamID}`}
              data-info-open={infoOpen ? "true" : "false"}
              aria-expanded={infoOpen}
              aria-label={`${infoOpen ? "Hide" : "Show"} ${workspaceName} integrity info`}
              title="Workspace info"
              // Aurora (Figma): the workspace NAME is the info trigger —
              // name + chevron, replacing the old tiny (i) icon button.
              className="flex-1 min-w-0 flex items-center gap-1.5 text-left group rounded-lg -mx-1 px-1 py-0.5 hover:bg-white/[0.04] transition-colors duration-150"
            >
              <span
                className="font-serif text-lg font-semibold text-theme-text-primary leading-tight tracking-tightish truncate"
                title={workspaceName}
              >
                {workspaceName}
              </span>
              <ChevronDown
                className="w-3.5 h-3.5 flex-shrink-0 text-theme-text-tertiary group-hover:text-theme-text-primary transition-colors duration-150"
                strokeWidth={2}
                aria-hidden
              />
            </button>
          ) : (
            <span
              className="flex-1 min-w-0 font-serif text-lg font-semibold text-theme-text-primary leading-tight tracking-tightish truncate"
              title={workspaceName}
            >
              {workspaceName}
            </span>
          )}
          {healthColor && (
            <span
              data-testid="conversation-list-health-dot"
              data-color={healthColor}
              aria-hidden
              className={[
                "w-2 h-2 rounded-full flex-shrink-0",
                healthColor === "green"
                  ? "bg-theme-success-text shadow-[0_0_6px_var(--theme-success-text)]"
                  : healthColor === "yellow"
                    ? "bg-theme-warning-text"
                    : healthColor === "red"
                      ? "bg-theme-error-border"
                      : "bg-theme-text-tertiary",
              ].join(" ")}
            />
          )}
        </div>
        <div className="font-mono text-[10px] text-theme-text-tertiary mt-1 truncate">
          {workspaceTeamID}
        </div>
      </div>

      {!loading && !error && conversations.length > 0 && (
        <div className="px-3 pt-3 flex-shrink-0">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] focus-within:border-theme-accent-link/50 transition-colors duration-150">
            <Search
              className="w-3.5 h-3.5 text-theme-text-tertiary flex-shrink-0"
              strokeWidth={1.75}
              aria-hidden
            />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter conversations"
              data-testid="conversation-filter"
              aria-label="Filter conversations"
              className="flex-1 min-w-0 bg-transparent border-none outline-none text-xs text-theme-text-primary placeholder:text-theme-text-tertiary"
            />
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto py-3 scrollbar-thin">
        {loading && (
          <div data-testid="conversation-list-loading" className="px-4 space-y-snug">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="flex items-center gap-2 py-1.5">
                <Skeleton variant="circle" width="0.875rem" height="0.875rem" />
                <Skeleton
                  variant="text"
                  width={`${8 + ((i * 3) % 7)}rem`}
                />
              </div>
            ))}
          </div>
        )}
        {error && !loading && (
          <p className="px-4 py-2 font-mono text-xs text-theme-error-text">
            {error}
          </p>
        )}
        {!loading && !error && conversations.length === 0 && (
          <EmptyState
            icon={<MessageSquare className="w-full h-full" strokeWidth={1.5} />}
            title="No conversations here yet"
            description="Conversations will appear after the next sync cycle."
            size="sm"
          />
        )}
        {!loading && !error && filterEmpty && (
          <p
            data-testid="conversation-filter-empty"
            className="px-4 py-2 font-mono text-xs text-theme-text-tertiary"
          >
            No conversations match “{filter.trim()}”.
          </p>
        )}
        {!loading && !error && conversations.length > 0 && SECTIONS.map(({ type, label }) => {
          const items = visibleGrouped[type];
          if (items.length === 0) return null;
          return (
            <section
              key={type}
              data-testid={`conversation-section-${type}`}
              data-section-count={items.length}
              className="mb-5"
            >
              <h3 className="px-4 mb-1.5 flex items-center gap-1.5 text-[11.5px] font-semibold text-theme-text-muted">
                <ChevronDown className="w-2.5 h-2.5" strokeWidth={2} aria-hidden />
                {label}
              </h3>
              <ul className="px-2 space-y-0.5">
                {items.map((c) => {
                  const isActive = c.id === activeConversationID;
                  const isEmpty = (c.message_count || 0) === 0;
                  const Icon = TYPE_ICON[c.type];
                  // S3.2: resolve group-DM (mpim) names to display names.
                  const displayLabel =
                    c.type === "mpim"
                      ? formatGroupDmLabel(c.label, handleMap)
                      : c.label;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(c.id)}
                        data-testid={`conversation-row-${c.id}`}
                        data-conversation-type={c.type}
                        data-empty={isEmpty ? "true" : "false"}
                        data-active={isActive ? "true" : "false"}
                        title={displayLabel}
                        className={[
                          "w-full px-2 py-[7px] text-left flex items-center gap-2.5 text-sm rounded-lg",
                          "transition-colors duration-150",
                          isActive
                            ? "bg-theme-bg-conv-active text-theme-accent-text"
                            : "text-theme-text-secondary hover:bg-theme-bg-conv-hover",
                          isEmpty ? "opacity-50" : "",
                        ].join(" ")}
                      >
                        {/* Aurora: DMs get a gradient avatar bubble keyed
                            by name; channels/groups keep their type icon. */}
                        {c.type === "im" ? (
                          <span
                            aria-hidden
                            style={{ background: gradientCSSFor(displayLabel) }}
                            className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-white"
                          >
                            {(displayLabel[0] ?? "?").toUpperCase()}
                          </span>
                        ) : (
                          <Icon
                            className={[
                              "w-3.5 h-3.5 flex-shrink-0",
                              isActive
                                ? "text-theme-accent-text"
                                : "text-theme-text-tertiary",
                            ].join(" ")}
                            strokeWidth={1.75}
                            aria-hidden
                          />
                        )}
                        <span className="truncate flex-1">{displayLabel}</span>
                        {!isEmpty && (
                          <span
                            className={[
                              "text-[10px] font-mono flex-shrink-0 px-1.5 py-px rounded-full",
                              isActive
                                ? "bg-theme-accent-bg text-white"
                                : "bg-white/[0.06] text-theme-text-tertiary",
                            ].join(" ")}
                            data-testid={`conversation-count-${c.id}`}
                          >
                            {c.message_count}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </nav>
  );
}
